/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
var PwcAiChat_1;
import { __decorate } from "tslib";
import { html, css, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { AiChatNetworkError, ChatMessageKind, Reaction, SupportedLocale, isTextMessagePart, } from '../model.js';
import { localize } from '../translations.js';
import { withEnterKey } from '../internal/with-enter-key.js';
import { prosAiChatUi } from '../pros-ai-chat-ui.js';
import { unreacheable } from '../internal/unreacheable.js';
import { copyToClipboard } from '../internal/clipboard.js';
import { areDiscussionSettingsEqual, cloneDiscussionSettings, getLastSessionMessageCount, } from '../internal/discussion-utils.js';
import * as Assets from '../assets.js';
import * as Styles from '../styles.js';
import { FeedbackModalState, } from './shared/pwc-feedback-modal.js';
/**
 * WebComponent.
 */
let PwcAiChat = PwcAiChat_1 = class PwcAiChat extends LitElement {
    constructor() {
        super(...arguments);
        this.locale = SupportedLocale.en_US;
        this.errorKind = AiChatNetworkError.NONE;
        /**
         * States.
         */
        this._notificationMessage = '';
        this._feedbackModalState = FeedbackModalState.CLOSED;
        /**
         * Internal Variables.
         */
        this._notificationTimeoutCloseHandler = -1;
        this._currentLastSessionMessageCount = -1;
    }
    /**
     * @inheritdoc
     */
    connectedCallback() {
        // eslint-disable-next-line wc/guard-super-call
        super.connectedCallback();
        // register.
        prosAiChatUi.registerPwcAiChat(this);
    }
    /**
     * @inheritdoc
     */
    disconnectedCallback() {
        // eslint-disable-next-line wc/guard-super-call
        super.disconnectedCallback();
        // unregister.
        prosAiChatUi.unregisterPwcAiChat(this);
    }
    /**
     * Render the elements composing a message.
     */
    static _renderMessageContent(message) {
        // render message parts.
        return message.parts.map(messagePart => html ` <div class="text">
          <pwc-message-part .messagePart=${messagePart}></pwc-message-part>
        </div>`);
    }
    /**
     * Render the actions in a message card.
     */
    _renderMessageActions(message) {
        // actions are greyed out if already used.
        const classReactionButton = message.reaction ? 'disabled' : 'enabled';
        // render icons.
        return html `<div class="actions">
      <div
        class="${classReactionButton}"
        @click=${() => this._handleThumbsUpButtonMouseClick(message)}
        @keydown=${(event) => withEnterKey(event, () => this._handleThumbsUpButtonMouseClick(message))}
      >
        ${when(message.reaction === Reaction.POSITIVE, () => Assets.iconSolidThumbsUp(), () => Assets.iconThumbsUp())}
        ${when(!message.reaction, () => html `<pwc-tooltip
              >${localize(this.locale, 'ai.chat.reaction.positive')}</pwc-tooltip
            >`, () => html `<pwc-tooltip
              >${localize(this.locale, 'ai.chat.reaction.already-provided')}</pwc-tooltip
            >`)}
      </div>
      <div
        class="${classReactionButton}"
        @click=${() => this._handleThumbsDownButtonMouseClick(message)}
        @keydown=${(event) => withEnterKey(event, () => this._handleThumbsDownButtonMouseClick(message))}
      >
        ${when(message.reaction === Reaction.NEGATIVE, () => Assets.iconSolidThumbsDown(), () => Assets.iconThumbsDown())}
        ${when(!message.reaction, () => html `<pwc-tooltip
              >${localize(this.locale, 'ai.chat.reaction.negative')}</pwc-tooltip
            >`, () => html `<pwc-tooltip
              >${localize(this.locale, 'ai.chat.reaction.already-provided')}</pwc-tooltip
            >`)}
      </div>
      <div
        @click=${(event) => this._handleCopyButtonAction(event.target)}
        @keydown=${(event) => withEnterKey(event, () => this._handleCopyButtonAction(event.target))}
      >
        <pwc-tooltip>${localize(this.locale, 'ai.chat.copy')}</pwc-tooltip>
        ${Assets.iconCopy()}
      </div>
    </div>`;
    }
    /**
     * Render an AI message.
     */
    _renderAiMessage(message) {
        return html ` <div class="message is-ai">
      <div class="pros-ai">
        ${Assets.iconCircleAi()}
        <div class="text">${localize(this.locale, 'ai.chat.pros-ai')}</div>
      </div>
      <div class="content card">
        ${PwcAiChat_1._renderMessageContent(message)}
        ${this._renderMessageActions(message)}
      </div>
    </div>`;
    }
    /**
     * Render a Welcome message.
     */
    _renderWelcomeMessage(message) {
        return html ` <div class="message is-ai">
      <div class="pros-ai">
        ${Assets.iconCircleAi()}
        <div class="text">${localize(this.locale, 'ai.chat.pros-ai')}</div>
      </div>
      <div class="content card">
        ${PwcAiChat_1._renderMessageContent(message)}
      </div>
    </div>`;
    }
    /**
     * Render a Suggested Prompts message.
     */
    _renderSuggestedPromptsMessage(message) {
        return html ` <div class="message is-suggested-prompts">
      <div class="title">
        ${localize(this.locale, 'ai.chat.suggested-prompts')}
      </div>
      <div class="content">
        ${message.parts.map(messagePart => html ` <div class="suggested-prompt-bubble">
              <div
                class="suggested-prompt-bubble-content"
                @click=${() => this._handleSelectSuggestedPrompt(messagePart)}
                @keydown=${(event) => withEnterKey(event, () => this._handleSelectSuggestedPrompt(messagePart))}
              >
                <div>${Assets.iconSparkleBadge()}</div>
                <pwc-message-part
                  .messagePart=${messagePart}
                ></pwc-message-part>
              </div>
            </div>`)}
      </div>
    </div>`;
    }
    /**
     * Render a Human message.
     */
    static _renderHumanMessage(message) {
        return html ` <div class="message is-human">
      <div class="content card">
        ${PwcAiChat_1._renderMessageContent(message)}
      </div>
    </div>`;
    }
    /**
     * Render a message in the discussion thread.
     */
    _renderMessage(message) {
        const { kind } = message;
        switch (kind) {
            case ChatMessageKind.AI:
                return this._renderAiMessage(message);
            case ChatMessageKind.HUMAN:
                return PwcAiChat_1._renderHumanMessage(message);
            case ChatMessageKind.SUGGESTED_PROMPTS:
                return this._renderSuggestedPromptsMessage(message);
            case ChatMessageKind.WELCOME_MESSAGE:
                return this._renderWelcomeMessage(message);
            default:
                return unreacheable(kind);
        }
    }
    /**
     * Render the 'new chat' separator.
     */
    _renderNewChatSeparator() {
        return html `
      <div class="new-chat-separator">
        <span></span>
        ${localize(this.locale, 'ai.chat.new-chat')}
        <span></span>
      </div>
    `;
    }
    /**
     * Render the discussion thread.
     */
    _renderDiscussion(discussion) {
        // vars.
        let currentSessionId;
        // trivial case: no discussion.
        if (!discussion) {
            return [];
        }
        // init the discussion with a welcome message.
        const result = [];
        // add additional messages.
        for (const chatSession of discussion.chatSessions) {
            // append a separator when the session id change.
            if (typeof currentSessionId === 'string') {
                if (currentSessionId !== chatSession.sessionId) {
                    result.push(this._renderNewChatSeparator());
                }
            }
            currentSessionId = chatSession.sessionId;
            // render the list of message for the given session.
            for (const message of chatSession.messages) {
                result.push(this._renderMessage(message));
            }
        }
        return result;
    }
    /**
     * Render 'waiting for response' loader.
     */
    _renderWaitingForResponseLoader() {
        if (!this.discussion) {
            return html ``;
        }
        if (!this.discussion.isWaitingForResponse) {
            return html ``;
        }
        return html ` <div class="message is-ai">
      <div class="pros-ai">
        ${Assets.iconCircleAi()}
        <div class="text">${localize(this.locale, 'ai.chat.pros-ai')}</div>
      </div>
      <div class="content card">
        <pwc-ai-loader .locale=${this.locale}></pwc-ai-loader>
      </div>
    </div>`;
    }
    /**
     * Render a notification, such as 'Copied to clibpoard'.
     */
    _renderNotification() {
        return html ` <div class="notification is-closed">
      ${Assets.iconCircleCheck()}
      <div class="text">${this._notificationMessage}</div>
    </div>`;
    }
    /**
     * Render the alert banner.
     */
    _renderAlertBanner() {
        // variables.
        let kind = 'is-error';
        let icon;
        let title;
        let description;
        let hasCopyButton;
        let hasRetryButton;
        let requestId;
        const { errorKind } = this;
        // alert banner icon.
        switch (errorKind) {
            case AiChatNetworkError.CONNECTIVITY_ISSUES:
                kind = 'is-error';
                icon = Assets.iconDanger();
                title = localize(this.locale, 'ai.chat.error.connectivity-issues');
                description = localize(this.locale, 'ai.chat.error.connectivity-issues.description');
                hasCopyButton = false;
                hasRetryButton = false;
                break;
            case AiChatNetworkError.GENERATING_RESPONSE:
                kind = 'is-warning';
                icon = Assets.iconWarning();
                title = localize(this.locale, 'ai.chat.error.generating-response');
                description = localize(this.locale, 'ai.chat.error.generating-response.description');
                hasCopyButton = false;
                hasRetryButton = false;
                break;
            case AiChatNetworkError.INTERNAL_SERVER_ERROR:
                kind = 'is-error';
                icon = Assets.iconDanger();
                title = localize(this.locale, 'ai.chat.error.internal-server-error');
                description = localize(this.locale, 'ai.chat.error.internal-server-error.description');
                hasCopyButton = true;
                hasRetryButton = true;
                break;
            case AiChatNetworkError.INVALID_REQUEST:
                kind = 'is-error';
                icon = Assets.iconDanger();
                title = localize(this.locale, 'ai.chat.error.invalid-request');
                description = localize(this.locale, 'ai.chat.error.invalid-request.description');
                hasCopyButton = true;
                hasRetryButton = true;
                break;
            case AiChatNetworkError.NONE:
                return html ``;
            default:
                unreacheable(errorKind);
        }
        // request id.
        if (this.errorId) {
            requestId = localize(this.locale, 'ai.chat.error.error-id', this.errorId);
        }
        return html `
      <div class="alert-banner ${kind}">
        ${icon}
        <div>
          <div class="text">
            <b> ${title}. </b>
            ${description}
            ${
        // request id.
        when(requestId, () => html `<br />${requestId}`)}
          </div>
          ${
        // retry button.
        when(hasRetryButton, () => html `<br /><button
                    class="retry-button"
                    @click=${() => this._handleRetryAction()}
                    @keydown=${(event) => withEnterKey(event, () => this._handleRetryAction())}
                  >
                    ${Assets.iconReload()}
                    ${localize(this.locale, 'ai.chat.retry')}
                  </button>`)}
        </div>
        ${
        // copy button rendering.
        when(hasCopyButton, () => html ` <div
                class="icon-copy-wrapper"
                @click=${() => this._handleCopyAlertBannerMessageAction()}
                @keydown=${(event) => withEnterKey(event, () => this._handleCopyAlertBannerMessageAction())}
              >
                <pwc-tooltip
                  >${localize(this.locale, 'ai.chat.copy')}</pwc-tooltip
                >
                ${Assets.iconCopy()}
              </div>`)}
      </div>
    `;
    }
    /**
     * Render the input box.
     */
    _renderInputBox() {
        return html ` <div class="input-box">
      ${this._renderNotification()} ${this._renderAlertBanner()}
      <pwc-user-input
        placeholder=${localize(this.locale, 'ai.chat.type-a-message')}
        .disableSendPrompt=${this.discussion?.isWaitingForResponse || false}
        @pwc-user-input-post-message=${(event) => this._emitPostMessage(event.detail.prompt)}
      ></pwc-user-input>
      <div class="disclaimer">
        ${localize(this.locale, 'ai.chat.history-disclaimer')}
      </div>
    </div>`;
    }
    /**
     * Render the output box.
     */
    _renderOutputBox() {
        return html ` <div class="output-box">
      <div class="discussion">
        ${this._renderDiscussion(this.discussion)}${this._renderWaitingForResponseLoader()}
      </div>
    </div>`;
    }
    /**
     * @override
     */
    render() {
        return html `
      <div class="container">
        ${this._renderOutputBox()}${this._renderInputBox()}
      </div>
      <pwc-feedback-modal
        .locale=${this.locale}
        .state=${this._feedbackModalState}
        @pwc-feedback-modal-close-event=${() => {
            this._handleFeedbackModalClose();
        }}
        @pwc-feedback-modal-submit-event=${(event) => {
            this._handleSubmitFeedback(event);
        }}
      ></pwc-feedback-modal>
    `;
    }
    /**
     * Scrolls to the latest message while keeping the user prompt in view so user knows what they asked
     */
    _scrollToLatestMessage(shouldFocusLastAiMessage) {
        // guard.
        if (!this.discussion) {
            return;
        }
        // guard
        if (!this._discussionElement) {
            return;
        }
        // get the latest message.
        let latestMessage = this._discussionElement.querySelector('div.message:last-of-type');
        // guard.
        if (!latestMessage) {
            return;
        }
        // trivial case: the last discussion contains a suggested prompt message.
        if (latestMessage.classList.contains('is-suggested-prompts')) {
            // get the latest message.
            latestMessage = [
                ...this._discussionElement.querySelectorAll('div.message.is-ai'),
            ].pop();
            // guard.
            if (!latestMessage) {
                throw new Error('Illagal State: no associated AI message found.');
            }
        }
        // trivial case: the discussion is read or the message is not an ai message.
        if (!shouldFocusLastAiMessage ||
            !latestMessage.classList.contains('is-ai')) {
            // scroll to the bottom instantly.
            this._discussionElement.scrollTop = this._discussionElement.scrollHeight;
            return;
        }
        // scroll to the latest message.
        if (latestMessage.classList.contains('is-ai')) {
            // smooth scrolls to the user prompt and shows top of the answer.
            this._discussionElement.scrollTo({
                top: latestMessage.offsetTop - 20,
                behavior: 'smooth',
            });
        }
    }
    /**
     * Manage scroll position.
     */
    _updateScrollPosition(previousDiscussionSettings, newDiscussionSettings, previousLastSessionMessageCount, newLastSessionMessageCount) {
        // manage scroll.
        if (this._discussionElement) {
            // if the discussions are not the same, scroll directly to the bottom.
            const isSameDiscussion = areDiscussionSettingsEqual(previousDiscussionSettings, newDiscussionSettings);
            if (isSameDiscussion) {
                // same discussion.
                // not the same message count, try focus last ai message.
                if (previousLastSessionMessageCount !== newLastSessionMessageCount) {
                    // note: if the number of messages has not been updated,
                    // no need to scroll (eg. when feedback flag is updated)
                    window.setTimeout(() => {
                        this._scrollToLatestMessage(true);
                    }, 100);
                }
            }
            else {
                // not the same discussion: scroll to the bottom.
                // and do not focus last ai message.
                window.setTimeout(() => {
                    this._scrollToLatestMessage(false);
                }, 100);
            }
        }
    }
    /**
     * @override
     */
    updated(_changedProperties) {
        if (_changedProperties.has('discussion')) {
            // when 'discussion' has been updated.
            if (this.discussion) {
                // compute new values.
                const newDiscussionSettings = cloneDiscussionSettings(this.discussion.settings);
                const newLastSessionMessageCount = getLastSessionMessageCount(this.discussion);
                // update the scroll position.
                if (this._currentDiscussionSettings) {
                    this._updateScrollPosition(this._currentDiscussionSettings, newDiscussionSettings, this._currentLastSessionMessageCount, newLastSessionMessageCount);
                }
                // update current values.
                this._currentDiscussionSettings = newDiscussionSettings;
                this._currentLastSessionMessageCount = newLastSessionMessageCount;
            }
        }
    }
    /**
     * Open a notification.
     */
    _openNotification(message) {
        // guard.
        if (!this._notificationElement) {
            return;
        }
        // reopen the notification.
        this._notificationElement.classList.remove('is-open');
        this._notificationElement.classList.add('is-closed');
        this._notificationElement.classList.remove('is-closed');
        this._notificationElement.classList.add('is-open');
        // reset timeout if any.
        if (this._notificationTimeoutCloseHandler > 0) {
            window.clearTimeout(this._notificationTimeoutCloseHandler);
        }
        // create a new timeout.
        this._notificationTimeoutCloseHandler = window.setTimeout(() => {
            // guard.
            if (!this._notificationElement) {
                return;
            }
            // close the notification.
            this._notificationElement.classList.remove('is-open');
            this._notificationElement.classList.add('is-closed');
        }, 2000);
        this._notificationMessage = message;
    }
    /**
     * Handle the event when the user close the feedback modal.
     */
    _handleFeedbackModalClose() {
        this._feedbackMessageReference = undefined;
        this._feedbackModalState = FeedbackModalState.CLOSED;
    }
    /**
     * Handle the event when the user submit its feedback.
     */
    _handleSubmitFeedback(event) {
        // guard.
        if (!this._feedbackMessageReference) {
            throw new Error('Illegal State: no reference message available.');
        }
        // emit the reaction.
        this._emitFeedback(event.detail.feedbackComment, this._feedbackMessageReference);
        // update values.
        this._feedbackMessageReference = undefined;
        this._feedbackModalState = FeedbackModalState.FEEDBACK_PROVIDED;
    }
    /**
     * Emit the reaction.
     */
    _emitReaction(reaction, message) {
        // guard.
        if (message.reaction) {
            throw new Error('Illegal State: reaction already sent.');
        }
        // create custom event.
        const event = new CustomEvent('pwc-ai-chat-user-reaction', {
            detail: {
                queryId: message.queryId,
                reaction,
            },
        });
        this.dispatchEvent(event);
    }
    /**
     * Emit the user feedback.
     */
    _emitFeedback(feedbackComment, message) {
        // create custom event.
        const event = new CustomEvent('pwc-ai-chat-user-feedback', {
            detail: {
                queryId: message.queryId,
                feedbackComment,
            },
        });
        this.dispatchEvent(event);
    }
    /**
     * Handle the thumbs up action.
     */
    _handleThumbsUpButtonMouseClick(message) {
        // guard.
        if (message.reaction) {
            return;
        }
        // emit the reaction.
        this._emitReaction(Reaction.POSITIVE, message);
    }
    /**
     * Handle the thumbs down action.
     */
    _handleThumbsDownButtonMouseClick(message) {
        // guard.
        if (message.reaction) {
            return;
        }
        // emit the reaction.
        this._emitReaction(Reaction.NEGATIVE, message);
        this._feedbackModalState = FeedbackModalState.PROVIDE_FEEDBACK;
        this._feedbackMessageReference = message;
    }
    /**
     * Handle the copy error banner mesage action.
     */
    _handleCopyAlertBannerMessageAction() {
        // guard.
        if (!this._alertBannerText) {
            return;
        }
        // copy to clipboard.
        copyToClipboard(this._alertBannerText.innerText);
    }
    /**
     * Handle the retry action.
     */
    _handleRetryAction() {
        // create custom event.
        const event = new CustomEvent('pwc-ai-chat-retry-post-message');
        // dispatch it.
        this.dispatchEvent(event);
    }
    /**
     * Handle select a suggested prompt.
     */
    _handleSelectSuggestedPrompt(messagePart) {
        // guard.
        if (!isTextMessagePart(messagePart)) {
            return;
        }
        // guard.
        if (!this._pwcUserInput) {
            return;
        }
        // set value.
        this._pwcUserInput.value = messagePart.data;
    }
    /**
     * Handle the copy action.
     */
    _handleCopyButtonAction(target) {
        // guard.
        if (target == null) {
            return;
        }
        // get parent message
        const messageElement = target.closest('.message');
        // guard.
        if (!messageElement) {
            return;
        }
        // get the message part elements.
        const pwcMessagePartElements = messageElement.querySelectorAll('pwc-message-part');
        // guard.
        if (!pwcMessagePartElements || pwcMessagePartElements.length === 0) {
            return;
        }
        // init variables.
        let plainText = '';
        let htmlText = '';
        // process all message parts.
        for (const pwcMessagePartElement of pwcMessagePartElements) {
            // get the copy content.
            const content = pwcMessagePartElement.getCopyContent();
            // concat content.
            plainText += content.plainText;
            htmlText += content.htmlText;
        }
        // open notification.
        this._openNotification(localize(this.locale, 'ai.chat.copied-to-clipboard'));
        // copy to clipboard.
        copyToClipboard(plainText, htmlText);
    }
    /**
     * Emit an event representing the post message.
     */
    _emitPostMessage(prompt) {
        // waiting for a response.
        if (this.errorKind === AiChatNetworkError.GENERATING_RESPONSE) {
            return;
        }
        // create custom event.
        const event = new CustomEvent('pwc-ai-chat-post-message', {
            detail: {
                prompt,
            },
        });
        // dispatch it.
        this.dispatchEvent(event);
    }
    /**
     * Clear the user input.
     */
    clearUserInput() {
        // guard.
        if (!this._pwcUserInput) {
            return;
        }
        // clean input.
        this._pwcUserInput.value = '';
    }
    /**
     * Get the scroll position.
     */
    getScrollPosition() {
        // initialize variables.
        let index = 0;
        let delta = 0;
        // guard.
        if (!this._discussionElement) {
            return undefined;
        }
        // adjust target (the middle of the scroll panel).
        const { scrollTop, clientHeight } = this._discussionElement;
        const target = scrollTop + clientHeight * 0.5;
        // get the list of messages in the screen.
        const messageElements = this._discussionElement.querySelectorAll('.message');
        // find the message crossing the target.
        for (const messageElement of messageElements) {
            // compute positions and dimensions.
            const messageTop = messageElement.offsetTop;
            const messageHeight = messageElement.offsetHeight * 1.1;
            const messageBottom = messageTop + messageHeight;
            // compare against the target.
            if (messageTop < target && messageBottom > target) {
                delta = (target - messageTop) / messageHeight;
                // found, do not continue.
                return { index, delta };
            }
            // update the index for next iteration.
            index += 1;
        }
        return undefined;
    }
    /**
     * Restore the scroll position.
     */
    restoreScrollPosition(position) {
        // guard.
        if (!position) {
            return;
        }
        // guard.
        if (!this._discussionElement) {
            return;
        }
        // adjust target (the middle of the scroll panel).
        const target = this._discussionElement.clientHeight * 0.5;
        // get the list of messages in the screen.
        const messageElements = this._discussionElement.querySelectorAll('.message');
        // guard.
        if (position.index >= messageElements.length) {
            return;
        }
        // get the focused message.
        const messageElement = messageElements[position.index];
        // compute positions and dimensions.
        const messageTop = messageElement.offsetTop;
        const messageOffset = messageElement.offsetHeight * 1.1 * position.delta;
        const scrollTop = messageTop + messageOffset - target;
        // update the scroll position.
        this._discussionElement.scrollTop = scrollTop;
    }
    /**
     * Set a prompt.
     */
    setPrompt(prompt) {
        // guard.
        if (!this._pwcUserInput) {
            return;
        }
        // set value.
        this._pwcUserInput.value = prompt;
    }
};
// styles.
PwcAiChat.styles = [
    css `
      :host {
        display: flex;
        flex: auto;

        /* font size */
        --pwc-ai-chat-font-size-x-small: var(--pwc-font-size-x-small, 0.75rem);
        --pwc-ai-chat-font-size-small: var(--pwc-font-size-small, 0.875rem);

        /* margins */
        --pwc-ai-chat-inner-margin: 11px;

        /* sizes */
        --pwc-ai-chat-message-width: 870px;
      }

      .container {
        display: flex;
        flex: auto;
        flex-direction: column;
        position: relative;
        background: linear-gradient(
          220deg,
          rgba(38, 169, 211, 0.05) 17.04%,
          rgba(11, 60, 234, 0.05) 30.45%,
          rgba(61, 23, 220, 0.05) 55.69%,
          rgba(255, 126, 59, 0.05) 77.2%
        );
      }

      /* Output Box */
      .output-box {
        display: flex;
        flex-direction: column;
        flex: auto;
        position: relative;
        min-height: 0;
      }

      .output-box .discussion {
        display: flex;
        flex-direction: column;
        flex: auto;
        height: 100%;
        overscroll-behavior: contain;
        overflow-y: auto;
        padding-bottom: 30px;
      }

      /* Input Box */
      .input-box {
        position: relative;
        display: flex;
        flex-direction: column;
        flex: none;
        min-height: 0px;
        align-items: center;
      }

      .input-box pwc-user-input {
        display: flex;
        flex: auto;
        position: relative;
        width: calc(
          var(--pwc-ai-chat-message-width) -
            (var(--pwc-ai-chat-inner-margin) * 4)
        );
        margin-top: var(--pwc-ai-chat-inner-margin);
        margin-bottom: var(--pwc-ai-chat-inner-margin);
        margin-left: auto;
        margin-right: auto;
        font-size: var(--pwc-ai-chat-font-size-small);
      }

      /* Dislaimer */
      .disclaimer {
        color: #808080;
        font-size: var(--pwc-ai-chat-font-size-x-small);
        margin: 0px 25px 13px 25px;
        text-align: center;
        width: calc(
          var(--pwc-ai-chat-message-width) -
            (var(--pwc-ai-chat-inner-margin) * 4)
        );
        margin-left: auto;
        margin-right: auto;
        line-height: 140%;
      }

      /* Input Box Notification */
      .input-box .notification {
        background-color: black;
        border-radius: 4px;
        box-shadow: 0px 3px 10px 0px rgba(0, 0, 0, 0.3);
        box-sizing: border-box;
        color: white;
        display: flex;
        flex-direction: row;
        max-width: 100%;
        padding: 10px;
        position: absolute;
        top: -40px;
      }

      .input-box .notification .icon-circle-check {
        color: #04b000;
        flex: none;
        height: 20px;
        width: 20px;
      }

      .input-box .notification .text {
        font-size: var(--pwc-ai-chat-font-size-small);
        line-height: 20px;
        margin-left: 10px;
        overflow-x: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .input-box .notification.is-open {
        visibility: visible;
      }

      .input-box .notification.is-closed {
        visibility: hidden;
      }

      /* Message */
      .message {
        display: flex;
        flex-direction: column;
        margin: 15px auto 25px auto;
        width: calc(
          var(--pwc-ai-chat-message-width) -
            (var(--pwc-ai-chat-inner-margin) * 4)
        );
      }

      .message.is-ai {
        align-items: flex-start;
      }

      .message.is-human {
        align-items: flex-end;
      }

      .message.is-suggested-prompts {
        align-items: flex-start;
      }

      .message .card {
        border-radius: 10px;
        box-shadow: 0px 3px 8px 0px rgba(0, 0, 0, 0.1);
        border: 1px solid #e0e0e0;
        max-width: 75%;
      }

      .message.is-human .card {
        background: #f0f9ff;
        margin-left: 30px;
      }

      .message.is-ai .card {
        background: white;
        max-width: 100%;
      }

      .message .pros-ai {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin: 0px 0px 8px 0px;
        width: 100%;
      }

      .message .pros-ai .icon-circle-ai {
        width: 22px;
        height: 22px;
      }

      .message .pros-ai .text {
        background: linear-gradient(
          350deg,
          #26a9d3 7.62%,
          #0b3cea 25.63%,
          #3d17dc 41.64%,
          #611096 59.71%,
          #a82c35 77.77%,
          #ff7e3b 88.44%
        );
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        font-size: var(--pwc-ai-chat-font-size-small);
        font-weight: 600;
        margin-left: 5px;
        margin-right: 5px;
      }

      .message.is-suggested-prompts .title {
        color: #707070;
        font-size: var(--pwc-ai-chat-font-size-x-small);
      }

      .message.is-suggested-prompts .content {
        align-items: flex-start;
        width: 75%;
      }

      .message.is-suggested-prompts .icon-sparkle-badge {
        width: 9px;
        height: 9px;
        margin-right: 7px;
      }

      .message.is-suggested-prompts .suggested-prompt-bubble {
        border-radius: 8px;
        background: linear-gradient(
          340deg,
          rgb(38, 169, 211) 17.04%,
          rgb(11, 60, 234) 30.45%,
          rgb(61, 23, 220) 55.69%,
          rgb(255, 126, 59) 77.2%
        );
        padding: 2px;
        margin-top: 10px;
      }

      .message.is-suggested-prompts .suggested-prompt-bubble-content {
        border-radius: 7px;
        background: linear-gradient(50deg, #fff8f5, #f5f4fd, #f4fbfd);
        display: flex;
        flex-direction: row;
        flex: auto;
        padding: 8px 10px;
        overflow-wrap: anywhere;
      }

      .message.is-suggested-prompts .suggested-prompt-bubble-content:hover {
        background: linear-gradient(353deg, #ffdadb, #d8dafe, #ccdef4);
        cursor: pointer;
      }

      .message .content {
        display: flex;
        flex-direction: column;
        flex: auto;
        color: #333;
        font-size: var(--pwc-ai-chat-font-size-small);
        line-height: 1.4rem;
      }

      .message .content .text {
        display: flex;
        flex: auto;
        padding: 0px 14px;
      }

      /* Message Actions */
      .message .content .actions {
        display: flex;
        flex: auto;
        flex-direction: row;
        padding: 10px 10px 10px 12px;
      }

      .message .content.text .actions {
        padding: 0px 10px 10px 12px;
      }

      .message .content .actions svg {
        width: 18px;
        height: 18px;
        margin: 7px 7px;
      }

      .message .content .actions svg.icon-copy {
        width: 16px;
        height: 16px;
        padding: 1px;
      }

      .message .actions div {
        border-radius: 50%;
        display: flex;
        flex: none;
        align-items: center;
        color: #444444;
        margin-left: 1px;
        margin-right: 1px;
      }

      .message .actions div:hover {
        background: #d9d6fe;
        cursor: pointer;
      }

      .message .actions div.disabled {
        background: none;
        color: #707070;
        cursor: unset;
      }

      .message .content.image .actions,
      .message .content.custom .actions {
        border-top: 1px solid #e0e0e0;
      }

      /* Alert Banner */
      .alert-banner {
        display: flex;
        flex: auto;
        flex-direction: row;
        border: 1px solid #e0e0e0;
        background-color: white;
        width: var(--pwc-ai-chat-message-width);
        box-sizing: border-box;
        font-size: var(--pwc-ai-chat-font-size-small);
        color: #444;
        padding: 10px 15px;
      }

      .alert-banner.is-error {
        background-color: #ffd6d6;
      }

      .alert-banner.is-warning {
        background-color: #fef7e1;
      }

      .alert-banner .text {
        display: inline-block;
        overflow-wrap: anywhere;
        text-align: left;
        vertical-align: top;
        line-height: 1.2rem;
      }

      .alert-banner .icon-danger {
        flex: none;
        width: 20px;
        height: 20px;
        color: #dc000d;
        padding: 0px 10px 0px 0px;
      }

      .alert-banner .icon-warning {
        flex: none;
        width: 20px;
        height: 20px;
        color: #f55f00;
        padding: 0px 10px 0px 0px;
      }

      .alert-banner .icon-copy-wrapper {
        flex: none;
        width: 15px;
        height: 15px;
        padding: 2px 0px 0px 10px;
        cursor: pointer;
      }

      .alert-banner .icon-copy {
        width: 15px;
        height: 15px;
      }

      /* New Chat Separator */
      .new-chat-separator {
        display: flex;
        flex-direction: row;
        flex: none;
        align-items: center;
        white-space: nowrap;
        color: #707070;
        font-size: var(--pwc-ai-chat-font-size-x-small);
        max-width: var(--pwc-ai-chat-message-width);
        margin: 0 auto 0 auto;
        width: 100%;
      }

      .new-chat-separator span {
        border-style: solid;
        border-width: 1px 0 0 0;
        border-color: #cccccc;
        width: 100%;
        margin-left: var(--pwc-ai-chat-inner-margin);
        margin-right: var(--pwc-ai-chat-inner-margin);
      }
    `,
    Styles.button,
];
__decorate([
    property({ attribute: false })
], PwcAiChat.prototype, "discussion", void 0);
__decorate([
    property({ attribute: 'locale', type: String })
], PwcAiChat.prototype, "locale", void 0);
__decorate([
    property({ attribute: false })
], PwcAiChat.prototype, "errorKind", void 0);
__decorate([
    property({ attribute: false })
], PwcAiChat.prototype, "errorId", void 0);
__decorate([
    query('.output-box .discussion', true)
], PwcAiChat.prototype, "_discussionElement", void 0);
__decorate([
    query('.input-box .notification', true)
], PwcAiChat.prototype, "_notificationElement", void 0);
__decorate([
    query('.alert-banner .text', false)
], PwcAiChat.prototype, "_alertBannerText", void 0);
__decorate([
    query('pwc-user-input')
], PwcAiChat.prototype, "_pwcUserInput", void 0);
__decorate([
    state()
], PwcAiChat.prototype, "_notificationMessage", void 0);
__decorate([
    state()
], PwcAiChat.prototype, "_feedbackModalState", void 0);
PwcAiChat = PwcAiChat_1 = __decorate([
    customElement('pwc-ai-chat')
], PwcAiChat);
export { PwcAiChat };
//# sourceMappingURL=pwc-ai-chat.js.map